/* Shared orbit showcase for the experiment and nail-care catalog. */
(function () {
  'use strict';
  const clamp = (n, min = 0, max = 1) => Math.max(min, Math.min(max, n));
  const blend = (a, b, t) => a + (b - a) * t;
  const smooth = (a, b, n) => { const t = clamp((n - a) / (b - a)); return t * t * (3 - 2 * t); };
  const tau = Math.PI * 2;
  // Completed turns are visually identical. Never interpolate their accumulated
  // count back to zero when handing the orbit over to scrolling.
  const phase = angle => ((angle + Math.PI) % tau + tau) % tau - Math.PI;
  // Viewport-height units: begin moving on the first scroll and reach a
  // readable first product within 40% of a screen, rather than 145%.
  // The copy becomes eligible once the collection title clears; its actual
  // reveal is coupled to the WebGL photograph below, not a second scroll threshold.
  const opening = { moveStart: .04, moveEnd: .36, fadeStart: .015, fadeEnd: .23, detailsStart: .23, first: .40 };

  // Keep the opening/closing brief while giving every instrument the same scroll distance.
  function timing(count, catalog = false) {
    const first = opening.first;
    const last = first + Math.max(0, count - 1) * .66;
    return { first, last, total: last + 1.28 };
  }
  function focusProgress(index, count, catalog = false) {
    const t = timing(count, catalog);
    return (t.first + clamp(index, 0, count - 1) * .66) / t.total;
  }

  function stepWindow(index, count) {
    const start = Math.floor(clamp(index, 0, Math.max(0, count - 1)) / 18) * 18;
    return { start, end: Math.min(start + 18, count) };
  }

  function firstDetailReveal(f, drumBlend, fallback) {
    if (!f.showDetails || f.active !== 0) return 1;
    const imageReveal = fallback ? smooth(.90, 1, f.morph) : drumBlend;
    // Reveal the copy during the final part of the photograph's own fade.
    // Both finish together; pending textures keep the copy hidden until ready.
    return smooth(.80, 1, imageReveal);
  }

  function dividerFrame(progress, count) {
    const schedule = timing(count, true);
    const p = clamp(progress) * schedule.total;
    // Lead the WebGL handoff (morph .90), without adding any scroll distance.
    // A pure playhead mapping makes reverse scroll and product jumps deterministic.
    const closing = 1 - smooth(schedule.last + .18, schedule.last + .50, p);
    const expansion = smooth(.14, .28, p) * closing;
    const cursor = clamp((p - schedule.first) / .66, 0, Math.max(0, count - 1));
    const step = Math.floor(cursor);
    // One eased half-turn per product, centred on the image/text change.
    // Share the held, smoothed playhead: stop with the product and reverse
    // directly with scrolling, without queuing independent spin animations.
    const turns = step + smooth(.2, .8, cursor - step);
    return {
      opacity: smooth(.08, .14, p) * (1 - smooth(schedule.last + .50, schedule.last + .60, p)),
      expansion,
      rotation: 360 * expansion + 180 * turns
    };
  }

  function heldOffset(distance) {
    if (distance <= .18) return 0;
    const t = clamp((distance - .18) / .32);
    // Zero speed/acceleration at the hold; unit speed and zero acceleration
    // at the boundary between products. Avoid the old power curve's sharp kick.
    return .5 * t * t * t * (7.44 + t * (-10.52 + t * 4.08));
  }

  // A small stretch of slow scrolling holds each photograph exactly at its
  // focus point. The scroll itself stays native; no wheel/touch events are blocked.
  function createFocusHold(count, catalog = false) {
    const schedule = timing(count, catalog);
    let lastRaw = null, lastShown = 0, cell = -1, direction = 0, bypass = false;
    return function presentationProgress(progress, velocity, viewportHeight) {
      const raw = clamp(progress);
      const position = raw * schedule.total;
      const movement = lastRaw === null ? 0 : Math.sign(raw - lastRaw);
      if (count < 2 || position < schedule.first || position > schedule.last) {
        lastRaw = raw; lastShown = raw; cell = -1;
        return raw;
      }
      const cursor = (position - schedule.first) / .66;
      const nearest = Math.round(cursor);
      const fast = Math.abs(velocity) > Math.max(700, viewportHeight * 1.1);
      // Once a deliberate gesture bypasses a stop, don't catch its slowing
      // momentum halfway through that same product. Reconsider at the next one.
      if (nearest !== cell || (movement && direction && movement !== direction)) {
        cell = nearest; bypass = fast;
      } else if (fast) bypass = true;
      if (movement) direction = movement;
      const offset = cursor - nearest;
      const distance = Math.abs(offset);
      const heldCursor = nearest + Math.sign(offset) * heldOffset(distance);
      let shown = bypass ? raw : (schedule.first + heldCursor * .66) / schedule.total;
      // Changing speed must never make a forward gesture visibly roll backward.
      if (lastRaw !== null) {
        if (movement > 0) shown = Math.max(lastShown, shown);
        else if (movement < 0) shown = Math.min(lastShown, shown);
        else shown = lastShown;
      }
      lastRaw = raw; lastShown = shown;
      return shown;
    };
  }

  function createItemSetters(gsap, item) {
    // quickSetter expands "scale" to the compound alias "scaleX,scaleY".
    // That alias is not a transform setter: address each axis explicitly.
    const scaleX = gsap.quickSetter(item, 'scaleX');
    const scaleY = gsap.quickSetter(item, 'scaleY');
    return {
      x: gsap.quickSetter(item, 'x', 'px'), y: gsap.quickSetter(item, 'y', 'px'),
      scale: value => { scaleX(value); scaleY(value); },
      opacity: gsap.quickSetter(item, 'opacity'), zIndex: gsap.quickSetter(item, 'zIndex')
    };
  }

  // Pure geometry, also exercised by the local regression check.
  function frame(progress, width, height, count, drift, catalog = false) {
    const mobile = width < 768;
    const t = timing(count, catalog);
    const p = clamp(progress) * t.total;
    const morph = smooth(opening.moveStart, opening.moveEnd, p) * (1 - smooth(t.last + .116, t.last + .928, p));
    const tour = count > 1 ? clamp((p - t.first) / (t.last - t.first)) : 0;
    const radius = (mobile ? Math.max(width * .8, height * .54) : height * .79) * Math.max(1, count / 8);
    const focusX = mobile ? width * .5 : width * .30;
    const focusY = mobile ? height * .30 : height * .49;
    const cx = blend(width / 2, focusX - radius, morph);
    const cy = blend(height * .49, focusY, morph);
    const overviewRx = width * (mobile ? .41 : .34);
    const overviewRy = height * (mobile ? .30 : .27);
    const rx = blend(overviewRx, radius, morph);
    const ry = blend(overviewRy, radius, morph);
    // The catalogue's phone photo leaves room beneath it for the divider line (and, on short phones, the price and button).
    const size = mobile ? Math.min(width * .80, height * (!catalog ? .35 : height < 720 ? .28 : .32)) : Math.min(width * .43, height * .65);
    // A conservative chord bound keeps adjacent thumbnails apart, including at the tight ends of the ellipse.
    const spacing = 2 * Math.min(overviewRx, overviewRy) * Math.sin(Math.PI / Math.max(2, count));
    const small = Math.min(mobile ? Math.min(width * .18, 85) : Math.min(width * .10, height * .14), spacing * .70);
    const rotation = -.20 * (1 - morph);
    const travel = tour * tau * (count - 1) / count;
    const active = Math.round(tour * (count - 1));
    const closing = p > t.last;
    const idle = phase(typeof drift === 'number' ? drift : drift[closing ? 'exit' : 'entry']);
    // Start a fresh overview at bottom centre, compensating for its tilt.
    // Once it has rotated, preserve that actual position until the normal
    // morph begins. There is no preliminary alignment or fixed entry path.
    const bottomAngle = Math.atan2(overviewRx * Math.cos(-.20), overviewRy * Math.sin(-.20));
    // Reduce the complete current phase BEFORE interpolating it. A hundred
    // turns and the same position within one turn must take the same path.
    // Opening and closing retain their own idle phase for reversible scrolling.
    const ringPhase = phase(bottomAngle + idle) * (1 - morph);
    const items = Array.from({ length: count }, (_, i) => {
      const angle = i / count * tau - travel + ringPhase;
      const distance = Math.acos(Math.cos(angle));
      const focusArc = Math.min(Math.PI / 3, tau / count * .80);
      const emphasis = distance < focusArc ? (Math.cos(distance / focusArc * Math.PI) + 1) / 2 : 0;
      const scale = blend(small / size, .20 + .80 * emphasis, morph);
      const ex = Math.cos(angle) * rx;
      const ey = Math.sin(angle) * ry;
      return {
        x: cx + ex * Math.cos(rotation) - ey * Math.sin(rotation) - size / 2,
        y: cy + ex * Math.sin(rotation) + ey * Math.cos(rotation) - size / 2,
        scale,
        opacity: blend(1, .12 + .88 * emphasis, morph),
        zIndex: Math.round(scale * 100)
      };
    });
    return { items, size, active, morph, showDetails: p >= opening.detailsStart && p <= t.last + .145, intro: 1 - smooth(opening.fadeStart, opening.fadeEnd, p), outro: smooth(t.last + .522, t.last + .986, p) };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { frame, timing, focusProgress, createItemSetters, createFocusHold, heldOffset, stepWindow, firstDetailReveal, dividerFrame };
  if (typeof document === 'undefined') return;

  const instances = new Map();
  const designInstances = new Map();
  function initDesigns(root) {
    if (designInstances.has(root)) return;
    const buttons = Array.from(root.querySelectorAll('[data-orbit-style-button]'));
    const sample = root.querySelector('[data-orbit-sample]');
    if (!buttons.length || !sample) return;
    const names = { editorial: 'Editorial', bold: 'Bold', atelier: 'Atelier', price: 'Price focus' };
    const url = new URL(window.location.href);
    let style = Object.hasOwn(names, url.searchParams.get('orbit-style')) ? url.searchParams.get('orbit-style') : 'editorial';
    sample.checked = url.searchParams.get('sample-rating') === '1';
    function apply(announce) {
      root.dataset.orbitStyle = style;
      buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.orbitStyleButton === style)));
      root.querySelectorAll('.sv-orbit__rating--real').forEach(rating => { rating.hidden = sample.checked; });
      root.querySelectorAll('.sv-orbit__rating--sample').forEach(rating => { rating.hidden = !sample.checked; });
      if (announce) {
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set('orbit-style', style);
        if (sample.checked) nextUrl.searchParams.set('sample-rating', '1');
        else nextUrl.searchParams.delete('sample-rating');
        window.history.replaceState(window.history.state, '', nextUrl);
        root.querySelector('[data-orbit-style-status]').textContent = `${names[style]} style. ${sample.checked ? 'Showing a labelled sample rating.' : 'Showing product review data.'}`;
        root.dispatchEvent(new Event('orbit:preview'));
      }
    }
    function onClick(event) { style = event.currentTarget.dataset.orbitStyleButton; apply(true); }
    function onSample() { apply(true); }
    buttons.forEach(button => button.addEventListener('click', onClick));
    sample.addEventListener('change', onSample);
    apply(false);
    designInstances.set(root, () => {
      buttons.forEach(button => button.removeEventListener('click', onClick));
      sample.removeEventListener('change', onSample);
    });
  }
  function init(root) {
    initDesigns(root);
    const host = root.closest('.sv-orbit-catalog');
    // Once the startup deadline has exposed the fallback, keep it stable.
    if (host?.dataset.orbitBoot === 'static' || instances.has(root)) return;
    if (!window.gsap || !window.ScrollTrigger) { host?.svOrbitReady?.(false); return; }
    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const track = root.querySelector('[data-orbit-track]');
    const stage = root.querySelector('[data-orbit-stage]');
    const items = Array.from(root.querySelectorAll('[data-orbit-item]'));
    const catalog = root.hasAttribute('data-orbit-catalog');
    if (!track || !items.length) { host?.svOrbitReady?.(false); return; }
    gsap.registerPlugin(ScrollTrigger);
    const mm = gsap.matchMedia();
    instances.set(root, mm);
    mm.add('(prefers-reduced-motion: no-preference) and (min-height: 600px)', () => {
      const intro = root.querySelector('[data-orbit-intro]');
      const outro = root.querySelector('[data-orbit-outro]');
      const details = Array.from(root.querySelectorAll('[data-orbit-detail]'));
      const detailContainer = root.querySelector('[data-orbit-details]');
      const divider = root.querySelector('[data-orbit-divider]');
      const dividerLine = divider?.querySelector('[data-orbit-divider-line]');
      const dividerPlus = divider?.querySelector('[data-orbit-divider-plus]');
      const steps = Array.from(root.querySelectorAll('[data-orbit-step]'));
      const stepNav = root.querySelector('.sv-orbit__steps');
      const stepPages = Array.from(root.querySelectorAll('[data-orbit-step-page]'));
      let stepStart = 0;
      function showStepWindow(index) {
        if (!catalog) return;
        const range = stepWindow(index, steps.length);
        stepStart = range.start;
        steps.forEach((button, i) => { button.hidden = i < range.start || i >= range.end; });
        stepPages.forEach(button => {
          button.disabled = Number(button.dataset.orbitStepPage) < 0 ? range.start === 0 : range.end === steps.length;
        });
      }
      function onStepPage(event) {
        showStepWindow(stepStart + Number(event.currentTarget.dataset.orbitStepPage) * 18);
        // Keep keyboard focus visible even if the page arrow has become disabled.
        steps[stepStart].focus({ preventScroll: true });
      }
      const art = root.querySelector('[data-orbit-art]');
      // Catalogue ring numbers live beside the thumbnails, never inside them:
      // a child of a 1/20-scaled item would raster blurry or oversized.
      const labels = Array.from(root.querySelectorAll('[data-orbit-label]'));
      const photos = items.map(item => item.querySelector('img'));
      const labelSetters = labels.map(label => ({ x: gsap.quickSetter(label, 'x', 'px'), y: gsap.quickSetter(label, 'y', 'px'), opacity: gsap.quickSetter(label, 'opacity') }));
      if (labels.length) gsap.set(labels, { xPercent: -50 });
      let ring = false, labelGap = 2, lastCover = -1;
      const motion = root.querySelector('[data-orbit-motion]');
      const hint = root.querySelector('[data-orbit-hint]');
      const bar = root.querySelector('[data-orbit-progress]');
      let paused = false, current = -1, width = 0, height = 0;
      const drift = { entry: 0, exit: 0 };
      const playhead = { value: 0 };
      const schedule = timing(items.length, catalog);
      const focusHold = createFocusHold(items.length, catalog);
      let inputProgress = 0;
      track.style.height = `${(schedule.total + 1) * 100}svh`;
      root.classList.add('is-animated');
      details.forEach(panel => { panel.inert = true; panel.setAttribute('aria-hidden', 'true'); });
      const setters = items.map(item => createItemSetters(gsap, item));
      let drum = null;
      let pendingRender = 0;
      let pendingJump = 0;
      let mounted = true;
      function invalidateDrum() {
        if (!mounted) return;
        if (!pendingRender) pendingRender = requestAnimationFrame(() => { pendingRender = 0; render(); });
      }
      if (window.SuvornaOrbitDrum) drum = window.SuvornaOrbitDrum.create({ THREE: window.THREE, art, items, invalidate: invalidateDrum });
      function render() {
        const presentation = playhead.value;
        const f = frame(presentation, width, height, items.length, drift, catalog);
        if (divider) {
          const seam = dividerFrame(presentation, items.length);
          // As tall as the photograph, which is the same square for every product
          // (suvorna-orbit-drum.js has the sum); the stylesheet keeps it inside the window.
          // These are resolved viewport measurements, not fixed design-pixel sizes.
          // Set on the stage: on phones the copy below the line is placed from it too.
          const fitSize = window.SuvornaOrbitDrum?.fitSize;
          divider.parentElement.style.setProperty('--orbit-divider-span', `${fitSize ? fitSize(f.size, width, height) : f.size * 1.25}px`);
          gsap.set(divider, { autoAlpha: seam.opacity });
          gsap.set(dividerLine, { scaleX: width < 768 ? seam.expansion : 1, scaleY: width < 768 ? 1 : seam.expansion });
          gsap.set(dividerPlus, { rotation: seam.rotation, xPercent: -50, yPercent: -50, x: 0, y: 0, transformOrigin: '50% 50%' });
        }
        // On phones the lower half belongs to the product copy, not passing thumbnails.
        art.style.clipPath = width < 768 ? `inset(0 0 ${51 * f.morph}% 0)` : '';
        const cursor = clamp((presentation * schedule.total - schedule.first) / .66, 0, items.length - 1);
        const drumBlend = drum ? drum.render(f, cursor, width, height) : 0;
        const reveal = firstDetailReveal(f, drumBlend, !drum || drum.isFallback(cursor));
        gsap.set(detailContainer, { opacity: reveal });
        // The first fade is driven by the image; avoid adding a separate CSS delay.
        details[0].style.transition = f.showDetails && f.active === 0 ? 'none' : '';
        f.items.forEach((item, i) => Object.keys(setters[i]).forEach(key => setters[i][key](key === 'opacity' ? item[key] * (1 - drumBlend) : item[key])));
        // Whole in the ring, the one covered square by the time the drum takes over
        // at morph .90: the stylesheet zooms each photo between the two on this.
        const cover = smooth(0, .9, f.morph);
        if (cover !== lastCover) { lastCover = cover; art.style.setProperty('--orbit-cover', cover.toFixed(4)); }
        // Numbers sit under each thumbnail's visual bottom edge and leave as
        // soon as the ring starts to open; the thumbnail itself fades later.
        const labelFade = 1 - smooth(0, .3, f.morph);
        labelSetters.forEach((label, i) => {
          const item = f.items[i];
          // The square item box holds a photo shown whole; hang the number off the
          // photo's own bottom edge, not the box's, so wide photos read as one unit.
          // It follows that edge down as the photo zooms to cover the box.
          const photo = photos[i];
          const aspect = photo?.naturalWidth && photo.naturalHeight ? photo.naturalWidth / photo.naturalHeight : 1;
          const shown = Math.min(1, Math.min(1, 1 / aspect) * (1 + (Math.max(aspect, 1 / aspect) - 1) * cover));
          label.x(item.x + f.size / 2);
          label.y(item.y + f.size / 2 + f.size * item.scale * shown / 2 + labelGap);
          label.opacity(item.opacity * (1 - drumBlend) * labelFade);
        });
        const inRing = f.morph < .5;
        if (inRing !== ring) { ring = inRing; root.classList.toggle('is-ring', ring); }
        gsap.set(intro, { autoAlpha: f.intro, xPercent: -50, yPercent: -50, x: 0, y: -20 * (1 - f.intro) });
        if (outro) {
          gsap.set(outro, { autoAlpha: f.outro, xPercent: -50, yPercent: -50, x: 0, y: 20 * (1 - f.outro) });
          outro.inert = f.outro < .9;
          outro.setAttribute('aria-hidden', String(f.outro < .9));
        }
        intro.setAttribute('aria-hidden', String(f.intro < .1));
        gsap.set(bar, { scaleX: clamp(inputProgress) });
        const next = f.showDetails && reveal > .001 ? f.active : -1;
        if (next !== current) {
          current = next;
          details.forEach((panel, i) => {
            panel.classList.toggle('is-active', i === current);
            panel.inert = i !== current;
            panel.setAttribute('aria-hidden', String(i !== current));
          });
          steps.forEach((button, i) => button.setAttribute('aria-current', String(i === current)));
          if (catalog) showStepWindow(current < 0 ? (f.outro > .5 ? items.length - 1 : 0) : current);
          if (current >= 0 && !catalog) {
            const button = steps[current];
            stepNav.scrollTo({ left: button.offsetLeft - (stepNav.clientWidth - button.clientWidth) / 2, behavior: 'instant' });
          }
        }
      }
      function measure() {
        width = stage.clientWidth;
        height = stage.clientHeight;
        labelGap = .2 * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 10);
        const size = frame(playhead.value, width, height, items.length, drift, catalog).size;
        gsap.set(items, { width: size, height: size });
        render();
      }
      measure();
      window.SuvornaMotion?.lenis?.resize();
      // Map native scroll to the centered hold FIRST, then smooth that target.
      // Smoothing before the hold reintroduces abrupt stops into a smooth input.
      const glide = gsap.quickTo(playhead, 'value', { duration: .4, ease: 'power2.out', onUpdate: render });
      let lastTarget = null;
      function updateScroll(self) {
        inputProgress = self.progress;
        gsap.set(bar, { scaleX: clamp(inputProgress) });
        const target = focusHold(inputProgress, self.getVelocity(), height);
        // Repeated slow input inside a hold must not restart the settling tween.
        if (lastTarget === null || Math.abs(target - lastTarget) > 1e-10) {
          lastTarget = target;
          glide(target);
        }
      }
      const trigger = ScrollTrigger.create({
        trigger: track, start: 'top top', end: 'bottom bottom',
        onUpdate: updateScroll,
        onRefresh: self => { measure(); updateScroll(self); }
      });
      function tick(time, delta) {
        const timeInTour = playhead.value * schedule.total;
        if (paused || document.hidden || window.scrollY < trigger.start - 1 || window.scrollY > trigger.end || (timeInTour > opening.fadeStart && timeInTour < schedule.last + .986)) return;
        const end = timeInTour > schedule.last ? 'exit' : 'entry';
        drift[end] = phase(drift[end] + Math.min(delta, 64) / 1000 * .14);
        render();
      }
      gsap.ticker.add(tick);
      // Initial geometry, render and scroll position are ready before reveal.
      host?.svOrbitReady?.(true);
      function onMotion() {
        paused = !paused;
        motion.setAttribute('aria-pressed', String(paused));
        motion.textContent = paused ? 'Play orbit' : 'Pause orbit';
      }
      function onStep(event) {
        event.preventDefault?.();
        const index = steps.indexOf(event.currentTarget);
        if (pendingJump) cancelAnimationFrame(pendingJump);
        // Let the browser finish focusing the clicked control before moving the page.
        pendingJump = requestAnimationFrame(() => {
          pendingJump = 0;
          const p = focusProgress(index, items.length, catalog);
          const top = trigger.start + (trigger.end - trigger.start) * p;
          // Use the host theme's existing Lenis; never create a second instance.
          const lenis = window.SuvornaMotion?.lenis;
          if (lenis) { lenis.resize(); lenis.scrollTo(top, { immediate: true, force: true }); }
          else window.scrollTo({ top, behavior: 'instant' });
          ScrollTrigger.update();
        });
      }
      // A thumbnail or its number is the same jump as the step number below.
      function ringTarget(event) {
        const target = event.target.closest('[data-orbit-item], [data-orbit-label]');
        if (!target || !art.contains(target)) return -1;
        return target.hasAttribute('data-orbit-label') ? labels.indexOf(target) : items.indexOf(target);
      }
      function onArtClick(event) {
        const index = ring ? ringTarget(event) : -1;
        if (index < 0 || !steps[index]) return;
        event.preventDefault();
        showStepWindow(index);
        steps[index].focus({ preventScroll: true });
        onStep({ currentTarget: steps[index] });
      }
      function onArtHover(event) {
        const index = ringTarget(event);
        labels.forEach((label, i) => label.classList.toggle('is-hover', event.type === 'pointerover' && i === index));
      }
      function onPreview() {
        // Compare the same instrument. Only leave the overview when no product is focused.
        if (current < 0) {
          const index = playhead.value > .5 ? items.length - 1 : 0;
          window.scrollTo({ top: trigger.start + (trigger.end - trigger.start) * focusProgress(index, items.length, catalog), behavior: 'instant' });
          ScrollTrigger.update();
        }
      }
      function onAnchor(event) {
        const anchor = event.target.closest('a[href="#orbit-selection"], a[href="#orbit-catalog-first"]');
        if (!anchor || !root.contains(anchor)) return;
        event.preventDefault();
        // The host Lenis anchor handler must not replace this mapped product stop.
        event.stopPropagation();
        if (anchor.hash === '#orbit-catalog-first') {
          showStepWindow(0);
          steps[0].focus({ preventScroll: true });
          onStep({ currentTarget: steps[0] });
          return;
        }
        const selection = root.querySelector('#orbit-selection');
        if (!selection) return;
        selection.scrollIntoView({ behavior: 'instant' });
        selection.focus({ preventScroll: true });
      }
      motion.addEventListener('click', onMotion);
      art.addEventListener('click', onArtClick);
      art.addEventListener('pointerover', onArtHover);
      art.addEventListener('pointerout', onArtHover);
      root.addEventListener('orbit:preview', onPreview);
      motion.title = 'Pause automatic rotation. Scrolling still moves through the instruments.';
      steps.forEach(button => button.addEventListener('click', onStep));
      stepPages.forEach(button => button.addEventListener('click', onStepPage));
      document.addEventListener('click', onAnchor);
      const resize = new ResizeObserver(() => { measure(); ScrollTrigger.refresh(); });
      resize.observe(stage);
      document.fonts.ready.then(() => { if (root.isConnected) ScrollTrigger.refresh(); });
      if (hint) hint.title = `Scroll down to move through all ${items.length} instruments; scroll up to reverse.`;
      return () => {
        mounted = false;
        glide.tween.kill();
        if (pendingRender) cancelAnimationFrame(pendingRender);
        if (pendingJump) cancelAnimationFrame(pendingJump);
        drum?.dispose();
        resize.disconnect();
        gsap.ticker.remove(tick);
        motion.removeEventListener('click', onMotion);
        art.removeEventListener('click', onArtClick);
        art.removeEventListener('pointerover', onArtHover);
        art.removeEventListener('pointerout', onArtHover);
        root.classList.remove('is-ring');
        labels.forEach(label => label.classList.remove('is-hover'));
        root.removeEventListener('orbit:preview', onPreview);
        steps.forEach(button => button.removeEventListener('click', onStep));
        stepPages.forEach(button => button.removeEventListener('click', onStepPage));
        document.removeEventListener('click', onAnchor);
        root.classList.remove('is-animated');
        track.style.height = '';
        art.style.clipPath = '';
        art.style.removeProperty('--orbit-cover');
        if (divider) divider.parentElement.style.removeProperty('--orbit-divider-span');
        if (catalog) intro.setAttribute('aria-hidden', 'false');
        if (outro) { outro.inert = !catalog; outro.setAttribute('aria-hidden', String(!catalog)); }
        current = -1;
        details[0].style.transition = '';
        details.forEach(panel => { panel.classList.remove('is-active'); panel.inert = !catalog; panel.setAttribute('aria-hidden', String(!catalog)); });
      };
    }, root);
    // The media preference/viewport may have changed while scripts loaded.
    if (!root.classList.contains('is-animated')) host?.svOrbitReady?.(false);
  }
  function boot() { document.querySelectorAll('[data-nail-orbit]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', event => {
    designInstances.forEach((cleanup, root) => {
      if (event.target.contains(root)) { cleanup(); designInstances.delete(root); }
    });
    instances.forEach((mm, root) => {
      if (event.target.contains(root)) { mm.revert(); instances.delete(root); }
    });
  });
})();
